import { type KeyboardEvent, type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EllipsisIcon } from "./review-icons";

export interface MenuAction {
  readonly key: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly danger?: boolean;
  /** A rule above it, as iOS divides a destructive item from the rest. */
  readonly divided?: boolean;
  readonly onSelect: () => void;
}

/** Room kept between the menu and the screen's edge. */
const EDGE = 8;

/**
 * A "…" button and its menu, as iOS's row `Menu`. The menu sits in a layer of
 * its own over the page, so a row that scrolls sideways does not clip it, and a
 * tap beside it only closes it, as on iOS.
 */
export function ActionsMenu({
  label,
  actions,
  disabled = false,
}: {
  label: string;
  actions: readonly MenuAction[];
  disabled?: boolean;
}) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const open = anchor !== null;

  const close = (refocus: boolean) => {
    setAnchor(null);
    if (refocus) button.current?.focus();
  };

  // Below the button, or above it when the screen ends first; its right edge on the button's, measured from the left
  // as the button is, so the room the page keeps for a scrollbar cannot move it.
  useLayoutEffect(() => {
    if (!anchor || !menu.current) return;
    const height = menu.current.offsetHeight;
    const below = anchor.bottom + 4;
    setTop(below + height > window.innerHeight - EDGE ? Math.max(EDGE, anchor.top - 4 - height) : below);
    setLeft(Math.max(EDGE, anchor.right - menu.current.offsetWidth));
    menu.current.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
  }, [anchor]);

  useEffect(() => {
    if (!open) return;
    const moved = () => setAnchor(null);
    window.addEventListener("resize", moved);
    window.addEventListener("scroll", moved, true);
    return () => {
      window.removeEventListener("resize", moved);
      window.removeEventListener("scroll", moved, true);
    };
  }, [open]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = [...(menu.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? [])];
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const focus = (i: number) => items[(i + items.length) % items.length]?.focus();
    if (e.key === "Escape" || e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      focus(at + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focus(at - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focus(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focus(items.length - 1);
    }
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        // Held, not disabled, while its row works: an action chosen from it keeps the focus here.
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (open) close(false);
          else if (!disabled) setAnchor(button.current?.getBoundingClientRect() ?? null);
        }}
        className="-mt-1.5 -mr-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-btn text-muted hover:bg-surface hover:text-white aria-disabled:opacity-40"
      >
        <EllipsisIcon />
      </button>
      {anchor &&
        createPortal(
          <>
            <div className="fixed inset-0 z-40" aria-hidden="true" onClick={() => close(false)} />
            <div
              ref={menu}
              id={menuId}
              role="menu"
              aria-label={label}
              tabIndex={-1}
              onKeyDown={onKeyDown}
              style={{ top, left }}
              className="fixed z-50 min-w-[11rem] rounded-row border border-line bg-app-elevated py-1 shadow-2xl"
            >
              {actions.map((action) => (
                <div key={action.key}>
                  {action.divided && <hr className="my-1 border-line" />}
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    onClick={() => {
                      close(true);
                      action.onSelect();
                    }}
                    className={`flex w-full items-center justify-between gap-6 px-3.5 py-2.5 text-left text-sm hover:bg-surface focus:bg-surface focus:outline-none ${
                      action.danger ? "text-danger" : "text-white"
                    }`}
                  >
                    {action.label}
                    <span className={action.danger ? "text-danger" : "text-muted"}>{action.icon}</span>
                  </button>
                </div>
              ))}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
